/**
 * JobsPage Component
 * Premium job management page with stats, pagination, and filtering
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, EmptyState, Spinner } from "../components/common";
import { ConfirmationModal } from "../components/common/ConfirmationModal";
import { JobTable } from "../components/jobs/JobTable";
import { JobCard } from "../components/jobs/JobCard";
import { CreateJobModal } from "../components/jobs/CreateJobModal";
import { useAppDispatch, useAppSelector, jobsActions } from "../redux";
import { ROUTES } from "../services/routes";
import {
  ArrowLeft,
  LayoutList,
  LayoutGrid,
  RefreshCw,
  Plus,
  BarChart3,
  CheckCircle2,
  Clock,
  XCircle,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  FileSearch,
} from "lucide-react";

export const JobsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { jobs, loading, pagination } = useAppSelector((state) => state.jobs);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [jobToDelete, setJobToDelete] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(jobsActions.fetchJobs({ page: 1, page_size: pageSize }));
  }, [dispatch, pageSize]);

  // Default to grid view on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode("grid");
      }
    };
    
    // Check initially
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleRefresh = () => {
    dispatch(jobsActions.fetchJobs({ page: currentPage, page_size: pageSize }));
  };

  const confirmDelete = (jobId: string) => {
    setJobToDelete(jobId);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!jobToDelete) return;
    try {
      setIsDeleting(true);
      await dispatch(jobsActions.deleteJob(jobToDelete)).unwrap();
      handleRefresh();
      setIsDeleteModalOpen(false);
      setJobToDelete(null);
    } catch (error) {
      console.error("Failed to delete job:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewJob = (jobId: string) => {
    navigate(ROUTES.JOB_DETAILS(jobId));
  };


  const handleJobCreated = () => {
    setCurrentPage(1);
    dispatch(jobsActions.fetchJobs({ page: 1, page_size: pageSize }));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    dispatch(jobsActions.fetchJobs({ page, page_size: pageSize }));
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    dispatch(jobsActions.fetchJobs({ page: 1, page_size: newSize }));
  };

  const sortedJobs = [...jobs].sort((a: any, b: any) => {
    return new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime();
  });

  // Pagination calculations using correct Redux field names
  const totalItems = pagination?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  // Stats
  const stats = [
    {
      label: "Total Jobs",
      value: totalItems,
      icon: <BarChart3 className="w-5 h-5" />,
      color: "primary",
    },
    {
      label: "Completed",
      value: jobs.filter((j: any) => j.status === "completed").length,
      icon: <CheckCircle2 className="w-5 h-5" />,
      color: "success",
    },
    {
      label: "Processing",
      value: jobs.filter((j: any) => j.status === "processing" || j.status === "pending").length,
      icon: <Clock className="w-5 h-5" />,
      color: "warning",
    },
    {
      label: "Failed",
      value: jobs.filter((j: any) => j.status === "failed").length,
      icon: <XCircle className="w-5 h-5" />,
      color: "danger",
    },
  ];

  const colorClasses: Record<string, { bg: string; text: string; icon: string }> = {
    primary: { bg: "from-primary-500/20 to-primary-600/20", text: "text-primary-400", icon: "bg-primary-500/20" },
    success: { bg: "from-emerald-500/20 to-emerald-600/20", text: "text-emerald-400", icon: "bg-emerald-500/20" },
    warning: { bg: "from-amber-500/20 to-amber-600/20", text: "text-amber-400", icon: "bg-amber-500/20" },
    danger: { bg: "from-red-500/20 to-red-600/20", text: "text-red-400", icon: "bg-red-500/20" },
  };

  return (
    <div className="min-h-screen bg-navy-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <button
              onClick={() => navigate(ROUTES.DASHBOARD)}
              className="flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span>Back to Dashboard</span>
            </button>

            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">All Jobs</h1>
            <p className="text-slate-400">Manage and monitor all your analysis jobs</p>
          </div>

          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-slate-800/50 backdrop-blur-sm rounded-xl p-1 border border-slate-700/50">
              <button
                onClick={() => setViewMode("table")}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === "table"
                    ? "bg-gradient-to-r from-primary-500/20 to-accent-500/20 text-primary-400"
                    : "text-slate-400 hover:text-white"
                }`}
                title="Table view"
              >
                <LayoutList className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === "grid"
                    ? "bg-gradient-to-r from-primary-500/20 to-accent-500/20 text-primary-400"
                    : "text-slate-400 hover:text-white"
                }`}
                title="Grid view"
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
            </div>

            <Button variant="outline" onClick={handleRefresh} icon={<RefreshCw className="w-4 h-4" />}>
              Refresh
            </Button>

            <Button
              variant="gradient"
              onClick={() => setIsCreateModalOpen(true)}
              icon={<Plus className="w-4 h-4" />}
            >
              New Job
            </Button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat) => {
            const colors = colorClasses[stat.color];
            return (
              <Card
                key={stat.label}
                padding="none"
                className={`bg-gradient-to-br ${colors.bg} border border-slate-700/30`}
              >
                <div className="p-4 flex items-center gap-3">
                  <div className={`p-2.5 rounded-xl ${colors.icon} ${colors.text}`}>
                    {stat.icon}
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">{stat.label}</p>
                    <p className={`text-2xl font-bold ${colors.text}`}>{stat.value}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Jobs List */}
        <Card variant="glass" padding="lg">
          {loading && jobs.length === 0 ? (
            <div className="text-center py-12">
              <Spinner size="xl" />
              <p className="text-slate-400 mt-4">Loading jobs...</p>
            </div>
          ) : sortedJobs.length === 0 ? (
            <EmptyState
              icon={<FileSearch className="w-12 h-12" />}
              title="No jobs found"
              description="Start by creating your first analysis job"
              action={{
                label: "Create Job",
                onClick: () => setIsCreateModalOpen(true),
              }}
            />
          ) : viewMode === "table" ? (
            <JobTable jobs={sortedJobs} loading={loading} onDelete={confirmDelete} onRefresh={handleRefresh} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedJobs.map((job: any) => (
                <JobCard key={job.id} job={job} onDelete={confirmDelete} onView={handleViewJob} />
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          {sortedJobs.length > 0 && (
            <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-4 border-t border-slate-700/50 pt-4">
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-sm">Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-1.5 text-white text-sm focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-slate-400 text-sm">per page</span>
              </div>

              {/* Page Info */}
              <div className="text-slate-400 text-sm">
                Showing {(currentPage - 1) * pageSize + 1} -{" "}
                {Math.min(currentPage * pageSize, pagination?.totalItems || sortedJobs.length)} of{" "}
                {pagination?.totalItems || sortedJobs.length} jobs
              </div>

              {/* Page Navigation */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                  icon={<ChevronFirst className="w-4 h-4" />}
                >
                  First
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  icon={<ChevronLeft className="w-4 h-4" />}
                >
                  Prev
                </Button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={loading}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                          currentPage === pageNum
                            ? "bg-gradient-to-r from-primary-500 to-accent-500 text-white"
                            : "bg-slate-800/50 text-slate-300 hover:bg-slate-700 border border-slate-700/50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                  iconRight={<ChevronRight className="w-4 h-4" />}
                >
                  Next
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages || loading}
                  iconRight={<ChevronLast className="w-4 h-4" />}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Create Job Modal */}
      <CreateJobModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleJobCreated}
        selectedJobType={null}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Job"
        message="Are you sure you want to delete this job? This action cannot be undone."
        confirmLabel="Delete"
        isLoading={isDeleting}
      />
    </div>
  );
};
